import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, X, MapPin, Crosshair, ChevronDown } from 'lucide-react'
import { apiFetch, ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'

type TerritoryKind = 'continent' | 'country' | 'subdivision' | 'locality'

interface Territory {
  id: string
  name: string
  kind: TerritoryKind
  parentId: string | null
  isoCode: string | null
}

interface BrokerTerritoryAssignment {
  id: string
  brokerId: string
  territoryId: string | null
  radiusCenterLat: number | null
  radiusCenterLng: number | null
  radiusKm: number | null
  radiusLabel: string | null
  radiusKmOverride: number | null
  isPrimary: boolean
  contactCompany: string | null
  contactName: string | null
  contactEmail: string | null
  contactPhone: string | null
  contactWebsite: string | null
  notes: string | null
  territory?: (Territory & { parent?: (Territory & { parent?: (Territory & { parent?: Territory | null }) | null }) | null }) | null
}

function describeAssignment(a: BrokerTerritoryAssignment): string {
  if (a.territory) {
    const chain: string[] = []
    let t: Territory | null | undefined = a.territory
    while (t) {
      chain.unshift(t.name)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      t = (t as any).parent ?? null
    }
    return chain.join(' › ')
  }
  if (a.radiusLabel) return a.radiusLabel
  if (a.radiusCenterLat != null && a.radiusCenterLng != null && a.radiusKm != null) {
    return `${a.radiusKm}km around (${a.radiusCenterLat.toFixed(3)}, ${a.radiusCenterLng.toFixed(3)})`
  }
  return '—'
}

export function TerritoryPicker({ brokerId }: { brokerId: string }) {
  const qc = useQueryClient()
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const { data: assignments, error, isLoading } = useQuery<BrokerTerritoryAssignment[], ApiClientError>({
    queryKey: ['broker-territories', brokerId],
    queryFn: () => artistFetch<BrokerTerritoryAssignment[]>(`/brokers/${brokerId}/territories`),
  })

  async function remove(assignmentId: string) {
    if (!window.confirm('Remove this territory from the broker?')) return
    try {
      await artistFetch(`/brokers/${brokerId}/territories/${assignmentId}`, { method: 'DELETE' })
      qc.invalidateQueries({ queryKey: ['broker-territories', brokerId] })
    } catch (err) {
      window.alert(err instanceof ApiClientError ? err.message : 'Failed to remove territory')
    }
  }

  return (
    <div className="space-y-4 rounded-lg border border-ovation-ink/10 bg-white p-5">
      <div>
        <h4 className="font-display text-base">Territories of responsibility</h4>
        <p className="text-xs text-ovation-muted">
          Click a territory to add a local contact (company, person, email, phone, website) —
          useful when the broker delegates that area to someone else.
        </p>
      </div>

      {isLoading && <p className="text-xs text-ovation-muted">Loading…</p>}
      {error && <p className="text-xs text-red-700">{error.message}</p>}

      {assignments && assignments.length === 0 && (
        <p className="text-xs italic text-ovation-muted">No territories assigned yet.</p>
      )}

      {assignments && assignments.length > 0 && (
        <ul className="space-y-2">
          {assignments.map((a) => {
            const isExpanded = expandedId === a.id
            const hasContact = !!(a.contactCompany || a.contactName || a.contactEmail || a.contactPhone || a.contactWebsite)
            return (
              <li
                key={a.id}
                className={`overflow-hidden rounded-md border text-xs ${
                  a.territoryId
                    ? 'border-ovation-ink/15 bg-ovation-ink/5'
                    : 'border-amber-300 bg-amber-50 text-amber-900'
                }`}
              >
                <button
                  type="button"
                  onClick={() => setExpandedId(isExpanded ? null : a.id)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left"
                >
                  {a.territoryId ? <MapPin className="h-3 w-3 shrink-0" /> : <Crosshair className="h-3 w-3 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="truncate">{describeAssignment(a)}</div>
                    {hasContact && (
                      <div className="truncate text-[10px] text-ovation-muted">
                        {a.contactCompany || a.contactName} {a.contactEmail ? `· ${a.contactEmail}` : ''}
                      </div>
                    )}
                  </div>
                  {a.isPrimary && (
                    <span className="rounded bg-emerald-100 px-1 py-0.5 text-[10px] text-emerald-800">
                      primary
                    </span>
                  )}
                  <ChevronDown
                    className={`h-3 w-3 shrink-0 text-ovation-muted transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                  />
                </button>

                {isExpanded && (
                  <AssignmentDetail
                    brokerId={brokerId}
                    assignment={a}
                    onRemove={() => remove(a.id)}
                  />
                )}
              </li>
            )
          })}
        </ul>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <AddNamedTerritory brokerId={brokerId} />
        <AddRadiusTerritory brokerId={brokerId} />
      </div>
    </div>
  )
}

function AddNamedTerritory({ brokerId }: { brokerId: string }) {
  const qc = useQueryClient()
  const [continentId, setContinentId] = useState('')
  const [countryId, setCountryId] = useState('')
  const [subdivisionId, setSubdivisionId] = useState('')
  const [localityId, setLocalityId] = useState('')
  const [adding, setAdding] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const { data: continents } = useQuery<Territory[]>({
    queryKey: ['territories', 'continents'],
    queryFn: () => apiFetch<Territory[]>('/territories?kind=continent'),
  })
  const { data: countries } = useQuery<Territory[]>({
    queryKey: ['territories', 'countries', continentId],
    queryFn: () => apiFetch<Territory[]>(`/territories?parentId=${continentId}`),
    enabled: !!continentId,
  })
  const { data: subdivisions } = useQuery<Territory[]>({
    queryKey: ['territories', 'subdivisions', countryId],
    queryFn: () => apiFetch<Territory[]>(`/territories?parentId=${countryId}`),
    enabled: !!countryId,
  })
  const { data: localities } = useQuery<Territory[]>({
    queryKey: ['territories', 'localities', subdivisionId],
    queryFn: () => apiFetch<Territory[]>(`/territories?parentId=${subdivisionId}`),
    enabled: !!subdivisionId,
  })

  const chosenId = localityId || subdivisionId || countryId || continentId

  async function onAdd() {
    if (!chosenId) {
      setErr('Choose a continent at minimum')
      return
    }
    setErr(null)
    setAdding(true)
    try {
      await artistFetch(`/brokers/${brokerId}/territories`, {
        method: 'POST',
        body: { territoryId: chosenId },
      })
      qc.invalidateQueries({ queryKey: ['broker-territories', brokerId] })
      setContinentId('')
      setCountryId('')
      setSubdivisionId('')
      setLocalityId('')
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : 'Failed to add')
    } finally {
      setAdding(false)
    }
  }

  return (
    <div className="space-y-2 rounded-md border border-ovation-ink/10 p-3">
      <label className="block text-xs uppercase tracking-wider text-ovation-muted">
        Named territory
      </label>

      <Cascade
        label="Continent"
        value={continentId}
        options={continents ?? []}
        onChange={(v) => {
          setContinentId(v)
          setCountryId('')
          setSubdivisionId('')
          setLocalityId('')
        }}
      />
      <Cascade
        label="Country"
        value={countryId}
        options={countries ?? []}
        onChange={(v) => {
          setCountryId(v)
          setSubdivisionId('')
          setLocalityId('')
        }}
        disabled={!continentId}
      />
      <Cascade
        label="Subdivision (state/county)"
        value={subdivisionId}
        options={subdivisions ?? []}
        onChange={(v) => {
          setSubdivisionId(v)
          setLocalityId('')
        }}
        disabled={!countryId}
        allowCreate={!!countryId}
        parentIdForCreate={countryId}
        kindForCreate="subdivision"
      />
      <Cascade
        label="Locality (city/town)"
        value={localityId}
        options={localities ?? []}
        onChange={setLocalityId}
        disabled={!subdivisionId && !countryId}
        allowCreate={!!(subdivisionId || countryId)}
        parentIdForCreate={subdivisionId || countryId}
        kindForCreate="locality"
      />

      {err && <p className="text-xs text-red-700">{err}</p>}
      <button
        type="button"
        onClick={onAdd}
        disabled={adding || !chosenId}
        className="inline-flex items-center gap-1 rounded-md bg-ovation-ink px-3 py-1.5 text-xs font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
      >
        <Plus className="h-3 w-3" />
        {adding ? 'Adding…' : 'Add territory'}
      </button>
    </div>
  )
}

function Cascade({
  label,
  value,
  options,
  onChange,
  disabled,
  allowCreate,
  parentIdForCreate,
  kindForCreate,
}: {
  label: string
  value: string
  options: Territory[]
  onChange: (v: string) => void
  disabled?: boolean
  allowCreate?: boolean
  parentIdForCreate?: string
  kindForCreate?: 'subdivision' | 'locality'
}) {
  const qc = useQueryClient()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const selectedName = options.find((o) => o.id === value)?.name ?? ''

  // Sync external value -> input text when it changes (e.g. parent reset)
  useEffect(() => {
    if (!open) setQuery(selectedName)
  }, [selectedName, open])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) {
        setOpen(false)
        setQuery(selectedName)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open, selectedName])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options.slice(0, 100)
    return options
      .filter((o) => o.name.toLowerCase().includes(q))
      .slice(0, 50)
  }, [options, query])

  const exactMatch = useMemo(
    () => options.find((o) => o.name.toLowerCase() === query.trim().toLowerCase()),
    [options, query],
  )

  const canCreate = allowCreate && !!query.trim() && !exactMatch && !!parentIdForCreate && !!kindForCreate

  async function doCreate() {
    if (!canCreate || creating) return
    setCreating(true)
    try {
      const created = await apiFetch<Territory>('/territories', {
        method: 'POST',
        body: { name: query.trim(), kind: kindForCreate, parentId: parentIdForCreate },
      })
      qc.invalidateQueries({ queryKey: ['territories'] })
      onChange(created.id)
      setQuery(created.name)
      setOpen(false)
    } catch (e) {
      window.alert(e instanceof ApiClientError ? e.message : 'Failed to add')
    } finally {
      setCreating(false)
    }
  }

  function pick(t: Territory) {
    onChange(t.id)
    setQuery(t.name)
    setOpen(false)
  }

  function clear() {
    onChange('')
    setQuery('')
    setOpen(false)
  }

  return (
    <div className="space-y-1">
      <span className="block text-[10px] uppercase tracking-wider text-ovation-muted">
        {label}
      </span>
      <div ref={wrapperRef} className="relative">
        <input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
            if (value) onChange('') // typing invalidates the previous selection
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canCreate) {
              e.preventDefault()
              doCreate()
            } else if (e.key === 'Enter' && exactMatch) {
              e.preventDefault()
              pick(exactMatch)
            } else if (e.key === 'Escape') {
              setOpen(false)
              setQuery(selectedName)
            }
          }}
          placeholder={disabled ? 'Pick parent first' : `Type to search ${kindForCreate ?? label.toLowerCase()}…`}
          disabled={disabled}
          className="w-full rounded-md border border-ovation-ink/15 px-2 py-1 pr-6 text-xs disabled:bg-ovation-ink/5 disabled:text-ovation-muted"
        />
        {value && !disabled && (
          <button
            type="button"
            onClick={clear}
            className="absolute right-1 top-1/2 -translate-y-1/2 text-ovation-muted hover:text-ovation-ink"
            title="Clear"
          >
            <X className="h-3 w-3" />
          </button>
        )}
        {open && !disabled && (
          <ul className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-y-auto rounded-md border border-ovation-ink/15 bg-white shadow-lg">
            {filtered.length === 0 && !canCreate && (
              <li className="px-2 py-1 text-xs italic text-ovation-muted">
                No matches
              </li>
            )}
            {filtered.map((o) => (
              <li key={o.id}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault()
                    pick(o)
                  }}
                  className={`block w-full px-2 py-1 text-left text-xs hover:bg-ovation-ink/5 ${
                    o.id === value ? 'bg-ovation-ink/5 font-medium' : ''
                  }`}
                >
                  {o.name}
                </button>
              </li>
            ))}
            {canCreate && (
              <li className="border-t border-ovation-ink/10">
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault()
                    doCreate()
                  }}
                  disabled={creating}
                  className="block w-full px-2 py-1 text-left text-xs text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                >
                  {creating ? 'Creating…' : `+ Create "${query.trim()}" as a new ${kindForCreate}`}
                </button>
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  )
}

type RadiusMode = 'address' | 'w3w' | 'coords'

interface GeocodeCapabilities {
  nominatim: boolean
  what3words: boolean
}

interface GeocodeResult {
  lat: number
  lng: number
  label: string
  provider: string
}

function AddRadiusTerritory({ brokerId }: { brokerId: string }) {
  const qc = useQueryClient()
  const [mode, setMode] = useState<RadiusMode>('address')
  const [addressQuery, setAddressQuery] = useState('')
  const [w3wQuery, setW3wQuery] = useState('')
  const [lat, setLat] = useState('')
  const [lng, setLng] = useState('')
  const [km, setKm] = useState('')
  const [label, setLabel] = useState('')
  const [adding, setAdding] = useState(false)
  const [locating, setLocating] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [resolved, setResolved] = useState<GeocodeResult | null>(null)

  const { data: caps } = useQuery<GeocodeCapabilities>({
    queryKey: ['geocode-caps'],
    queryFn: () => apiFetch<GeocodeCapabilities>('/geocode/capabilities'),
    staleTime: Infinity,
  })

  async function locate() {
    setErr(null)
    setResolved(null)
    setLocating(true)
    try {
      let result: GeocodeResult
      if (mode === 'address') {
        if (!addressQuery.trim()) throw new Error('Type an address or postcode')
        result = await apiFetch<GeocodeResult>(
          `/geocode/address?q=${encodeURIComponent(addressQuery.trim())}`,
        )
      } else if (mode === 'w3w') {
        if (!w3wQuery.trim()) throw new Error('Type three words separated by dots')
        result = await apiFetch<GeocodeResult>(
          `/geocode/w3w?words=${encodeURIComponent(w3wQuery.trim())}`,
        )
      } else {
        return
      }
      setResolved(result)
      setLat(String(result.lat))
      setLng(String(result.lng))
      if (!label.trim()) setLabel(result.label.slice(0, 120))
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : e instanceof Error ? e.message : 'Locate failed')
    } finally {
      setLocating(false)
    }
  }

  async function onAdd() {
    const latN = Number(lat)
    const lngN = Number(lng)
    const kmN = Number(km)
    if (!isFinite(latN) || !isFinite(lngN) || !isFinite(kmN) || kmN <= 0) {
      setErr('Lat, lng, and radius (km) all required')
      return
    }
    setErr(null)
    setAdding(true)
    try {
      await artistFetch(`/brokers/${brokerId}/territories`, {
        method: 'POST',
        body: {
          radiusCenterLat: latN,
          radiusCenterLng: lngN,
          radiusKm: kmN,
          radiusLabel: label.trim() || undefined,
        },
      })
      qc.invalidateQueries({ queryKey: ['broker-territories', brokerId] })
      setAddressQuery('')
      setW3wQuery('')
      setLat('')
      setLng('')
      setKm('')
      setLabel('')
      setResolved(null)
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : 'Failed to add')
    } finally {
      setAdding(false)
    }
  }

  return (
    <div className="space-y-2 rounded-md border border-ovation-ink/10 p-3">
      <label className="block text-xs uppercase tracking-wider text-ovation-muted">
        Radius territory
      </label>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-ovation-ink/10">
        <TabBtn active={mode === 'address'} onClick={() => setMode('address')}>
          Address / postcode
        </TabBtn>
        {caps?.what3words && (
          <TabBtn active={mode === 'w3w'} onClick={() => setMode('w3w')}>
            what3words
          </TabBtn>
        )}
        <TabBtn active={mode === 'coords'} onClick={() => setMode('coords')}>
          Coordinates
        </TabBtn>
      </div>

      {mode === 'address' && (
        <div className="flex gap-1 pt-1">
          <input
            value={addressQuery}
            onChange={(e) => setAddressQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                locate()
              }
            }}
            placeholder="123 Main St, London W1 / SW1A 1AA"
            className="flex-1 rounded-md border border-ovation-ink/15 px-2 py-1 text-xs"
          />
          <button
            type="button"
            onClick={locate}
            disabled={locating}
            className="rounded-md bg-ovation-ink px-2 py-1 text-xs text-ovation-paper disabled:opacity-50"
          >
            {locating ? '…' : 'Locate'}
          </button>
        </div>
      )}

      {mode === 'w3w' && (
        <div className="flex gap-1 pt-1">
          <input
            value={w3wQuery}
            onChange={(e) => setW3wQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                locate()
              }
            }}
            placeholder="filled.count.soap"
            className="flex-1 rounded-md border border-ovation-ink/15 px-2 py-1 text-xs"
          />
          <button
            type="button"
            onClick={locate}
            disabled={locating}
            className="rounded-md bg-ovation-ink px-2 py-1 text-xs text-ovation-paper disabled:opacity-50"
          >
            {locating ? '…' : 'Locate'}
          </button>
        </div>
      )}

      {mode === 'coords' && (
        <div className="grid grid-cols-2 gap-2 pt-1">
          <input
            value={lat}
            onChange={(e) => setLat(e.target.value)}
            placeholder="Latitude"
            className="rounded-md border border-ovation-ink/15 px-2 py-1 text-xs"
          />
          <input
            value={lng}
            onChange={(e) => setLng(e.target.value)}
            placeholder="Longitude"
            className="rounded-md border border-ovation-ink/15 px-2 py-1 text-xs"
          />
        </div>
      )}

      {resolved && (
        <p className="rounded bg-emerald-50 px-2 py-1 text-[10px] text-emerald-900">
          Found: {resolved.label} ({resolved.lat.toFixed(4)}, {resolved.lng.toFixed(4)})
        </p>
      )}

      <div className="grid grid-cols-2 gap-2">
        <input
          value={km}
          onChange={(e) => setKm(e.target.value)}
          placeholder="Radius (km)"
          className="rounded-md border border-ovation-ink/15 px-2 py-1 text-xs"
        />
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Label (optional)"
          className="rounded-md border border-ovation-ink/15 px-2 py-1 text-xs"
        />
      </div>

      {err && <p className="text-xs text-red-700">{err}</p>}

      <button
        type="button"
        onClick={onAdd}
        disabled={adding || !lat || !lng || !km}
        className="inline-flex items-center gap-1 rounded-md bg-ovation-ink px-3 py-1.5 text-xs font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
      >
        <Plus className="h-3 w-3" />
        {adding ? 'Adding…' : 'Add radius'}
      </button>
    </div>
  )
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px border-b-2 px-2 py-1 text-xs ${
        active
          ? 'border-ovation-ink font-medium text-ovation-ink'
          : 'border-transparent text-ovation-muted hover:text-ovation-ink'
      }`}
    >
      {children}
    </button>
  )
}

function AssignmentDetail({
  brokerId,
  assignment,
  onRemove,
}: {
  brokerId: string
  assignment: BrokerTerritoryAssignment
  onRemove: () => void
}) {
  const qc = useQueryClient()
  const [contactCompany, setContactCompany] = useState(assignment.contactCompany ?? '')
  const [contactName, setContactName] = useState(assignment.contactName ?? '')
  const [contactEmail, setContactEmail] = useState(assignment.contactEmail ?? '')
  const [contactPhone, setContactPhone] = useState(assignment.contactPhone ?? '')
  const [contactWebsite, setContactWebsite] = useState(assignment.contactWebsite ?? '')
  const [notes, setNotes] = useState(assignment.notes ?? '')
  const [radiusKmOverride, setRadiusKmOverride] = useState(
    assignment.radiusKmOverride != null ? String(assignment.radiusKmOverride) : '',
  )
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const isRadius = !assignment.territoryId
  // Override only relevant for named sub-country territories
  const showRadiusOverride =
    assignment.territoryId &&
    assignment.territory &&
    (assignment.territory.kind === 'subdivision' || assignment.territory.kind === 'locality')

  async function save() {
    setErr(null)
    setSaving(true)
    try {
      await artistFetch(`/brokers/${brokerId}/territories/${assignment.id}`, {
        method: 'PATCH',
        body: {
          contactCompany: contactCompany.trim() || null,
          contactName: contactName.trim() || null,
          contactEmail: contactEmail.trim() || null,
          contactPhone: contactPhone.trim() || null,
          contactWebsite: contactWebsite.trim() || null,
          notes: notes.trim() || null,
          ...(showRadiusOverride
            ? { radiusKmOverride: radiusKmOverride ? Number(radiusKmOverride) : null }
            : {}),
        },
      })
      qc.invalidateQueries({ queryKey: ['broker-territories', brokerId] })
      qc.invalidateQueries({ queryKey: ['broker-coverage'] })
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-3 border-t border-ovation-ink/10 bg-white p-3">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Field label="Company">
          <input
            value={contactCompany}
            onChange={(e) => setContactCompany(e.target.value)}
            placeholder="e.g. GeekyBee"
            className="w-full rounded-md border border-ovation-ink/15 px-2 py-1 text-xs"
          />
        </Field>
        <Field label="Contact name">
          <input
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            placeholder="e.g. Graham Ford"
            className="w-full rounded-md border border-ovation-ink/15 px-2 py-1 text-xs"
          />
        </Field>
        <Field label="Email">
          <input
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="graham@geekybee.net"
            className="w-full rounded-md border border-ovation-ink/15 px-2 py-1 text-xs"
          />
        </Field>
        <Field label="Phone">
          <input
            type="tel"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            placeholder="+44 7xxx xxx xxx"
            className="w-full rounded-md border border-ovation-ink/15 px-2 py-1 text-xs"
          />
        </Field>
        <Field label="Website" className="sm:col-span-2">
          <input
            type="url"
            value={contactWebsite}
            onChange={(e) => setContactWebsite(e.target.value)}
            placeholder="https://geekybee.net"
            className="w-full rounded-md border border-ovation-ink/15 px-2 py-1 text-xs"
          />
        </Field>
        {showRadiusOverride && (
          <Field label="Radius (km, override)">
            <input
              type="number"
              min={1}
              value={radiusKmOverride}
              onChange={(e) => setRadiusKmOverride(e.target.value)}
              placeholder={assignment.territory?.kind === 'locality' ? '30' : '200'}
              className="w-full rounded-md border border-ovation-ink/15 px-2 py-1 text-xs"
            />
          </Field>
        )}
        <Field label="Notes" className="sm:col-span-2">
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Anything else worth recording about this area"
            className="w-full rounded-md border border-ovation-ink/15 px-2 py-1 text-xs"
          />
        </Field>
      </div>

      {err && <p className="text-xs text-red-700">{err}</p>}

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onRemove}
          className="text-xs text-red-700 hover:underline"
        >
          {isRadius ? 'Remove radius' : 'Remove territory'}
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="rounded-md bg-ovation-ink px-3 py-1 text-xs font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  )
}

function Field({
  label,
  children,
  className,
}: {
  label: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <label className={`block ${className ?? ''}`}>
      <span className="mb-1 block text-[10px] uppercase tracking-wider text-ovation-muted">
        {label}
      </span>
      {children}
    </label>
  )
}
